<template>
	<div class="user-page-container">
		<div class="user-search-container">
			<input class="user-search-input" type="text" v-model="this.searchKeyword"
			       placeholder="输入邮箱 / 设备ID搜索" @keyup.enter="onSearchClicked">
			<div class="header-btn user-search-btn" @click="onSearchClicked">搜索</div>
			<div class="header-btn user-search-btn" @click="onResetSearchClicked">重置</div>
		</div>

		<div class="btn-container">
			<div class="user-page-title">{{ this.getListTitle() }}</div>

			<a-pagination v-model:current="this.page" simple :pageSize="this.pageSize"
			              :total="this.totalCount" @change="onPageChanged"
			              :showSizeChanger="false"/>

			<div class="header-btn user-search-btn" @click="onRefreshBtnClicked">刷新</div>
		</div>

		<div class="cs-main-container">
			<div class="list-container">
				<div class="user-empty" v-if="this.isLoaded === true && this.userList.length === 0">
					{{ this.isSearch === true ? '没有搜到匹配的用户' : '还没有用户' }}
				</div>

				<template v-for="item in this.userList" :key="item.email || item.userId">
					<div class="item-container user-row">
						<div class="user-email-line">
							<span class="user-email-text">{{ item.email || '无邮箱' }}</span>
							<img src="../images/copy_light.svg" alt="复制" class="copy-text-btn"
							     title="复制邮箱" v-if="item.email"
							     @click="onCopyText(item.email, '邮箱')">
							<span :class="['user-ban-tag', item.isBanned === true ? 'is-banned' : 'is-normal']">
								{{ item.isBanned === true ? '已禁用' : '正常' }}
							</span>
							<!-- 禁言独立于封禁：一个账号可能"正常"但被禁言，两个标签要能同时出现 -->
							<span class="user-ban-tag is-muted" v-if="item.isMuted === true"
							      :title="item.muteUntilTimeString ? `解禁时间 ${item.muteUntilTimeString}` : ''">
								已禁言
							</span>
							<!-- 限速独立于封禁/禁言：账号能正常用，只是 VPN 上下行被限到后台设的值 -->
							<span class="user-ban-tag is-limited" v-if="item.rateLimit"
							      :title="item.rateLimit.expireTimeString ? `限速至 ${item.rateLimit.expireTimeString}` : '永久限速'">
								限速 ↑{{ this.formatRateKbps(item.rateLimit.upKbps) }} ↓{{ this.formatRateKbps(item.rateLimit.downKbps) }}
							</span>
						</div>

						<div class="user-field-line">
							<span class="user-field-label">会员剩余</span>
							<span :style="this.getPlanStyle(item)">{{ this.getPlanLeftText(item) }}</span>
						</div>

						<div class="user-field-line">
							<span class="user-field-label">注册时间</span>
							<span>{{ this.formatTimeString(item.registerTime) }}</span>
						</div>

						<div class="user-field-line">
							<span class="user-field-label">上次登录</span>
							<span>{{ this.formatTimeString(item.lastLoginTime) }}</span>
						</div>

						<div class="user-field-line user-device-line">
							<span class="user-field-label">设备ID</span>
							<div class="user-device-list">
								<div class="user-device-empty" v-if="this.getDeviceIdList(item).length === 0">无</div>
								<div class="user-device-item" v-for="deviceId in this.getDeviceIdList(item)"
								     :key="deviceId">
									<span class="user-device-id">{{ deviceId }}</span>
									<img src="../images/copy_light.svg" alt="复制" class="copy-text-btn"
									     title="复制设备ID" @click="onCopyText(deviceId, '设备ID')">
								</div>
								<!-- 只有一台的时候不用报数。台数放在列表末尾而不是标签里：
								     标签那一列是固定宽度的，塞进去会把右边几行的对齐挤散 -->
								<div class="user-device-count" v-if="this.getDeviceIdList(item).length > 1">
									共 {{ this.getDeviceIdList(item).length }} 台
								</div>
							</div>
						</div>

						<div class="user-action-line">
							<div :class="['header-btn', 'user-add-btn',
							              this.addingEmail === item.email ? 'is-updating' : '']"
							     @click="onAddMembershipClicked(item)">
								Add Membership
							</div>

							<!-- 每一行都给按钮。"无套餐"的账号点下去只是把一个本来就空的字段再置空一次，
							     没有副作用；按行显示与否反而让人以为这条记录有什么特别 -->
							<a-popconfirm :title="this.getClearConfirmText(item)"
							              ok-text="确认清空" cancel-text="取消" @confirm="onClearMembership(item)">
								<div :class="['header-btn', 'user-clear-btn',
								              this.clearingEmail === item.email ? 'is-updating' : '']">
									Clear Membership
								</div>
							</a-popconfirm>

							<!-- 限速：作用于该账号下所有设备。已限速的换成取消按钮，同一位置只放当前能做的动作 -->
							<div v-if="!item.rateLimit" :class="['header-btn', 'user-limit-btn',
							                                      this.limitingEmail === item.email ? 'is-updating' : '']"
							     @click="onOpenLimitModal(item)">
								Limit
							</div>
							<a-popconfirm v-else :title="`确认取消 ${item.email} 的限速？（在线设备立即恢复）`"
							              ok-text="确认取消" cancel-text="返回" @confirm="onClearRateLimit(item)">
								<div :class="['header-btn', 'user-unlimit-btn',
								              this.limitingEmail === item.email ? 'is-updating' : '']">
									Unlimit
								</div>
							</a-popconfirm>

							<!-- 禁言和封禁是两件事：这个只堵客服渠道的发言，账号照常能用，
							     所以已封禁的行也照样给按钮 -->
							<a-popconfirm v-if="item.isMuted !== true" :title="this.getMuteConfirmText(item)"
							              ok-text="确认禁言" cancel-text="取消" @confirm="onMuteUser(item)">
								<div :class="['header-btn', 'user-mute-btn',
								              this.mutingEmail === item.email ? 'is-updating' : '']">
									Mute
								</div>
							</a-popconfirm>
							<!-- 已禁言的换成解除按钮。解禁时间写在按钮下面：禁言会自己到期，
							     客服要回答"我什么时候能说话"这个问题 -->
							<div class="user-muted-text" v-else>
								<a-popconfirm :title="this.getUnmuteConfirmText(item)"
								              ok-text="确认解除" cancel-text="取消" @confirm="onUnmuteUser(item)">
									<div :class="['header-btn', 'user-unmute-btn',
									              this.mutingEmail === item.email ? 'is-updating' : '']">
										Unmute
									</div>
								</a-popconfirm>
								<div class="user-muted-sub" v-if="item.muteUntilTimeString">
									禁言至 {{ item.muteUntilTimeString }}
								</div>
							</div>

							<!-- 没封的给 Block，封了的换成 Unblock：同一个位置只出现当前能做的那一个动作 -->
							<a-popconfirm v-if="item.isBanned !== true" :title="this.getBlockConfirmText(item)"
							              ok-text="确认封禁" cancel-text="取消" @confirm="onBlockUser(item)">
								<div :class="['header-btn', 'user-block-btn',
								              this.blockingEmail === item.email ? 'is-updating' : '']">
									Block
								</div>
							</a-popconfirm>
							<!-- 封禁原因跟在解封按钮下面。被别人的记录连坐时要说清是哪个账号带上的，
							     不然会在这条记录上找不到对应的原因 -->
							<div class="user-blocked-text" v-else>
								<a-popconfirm :title="this.getUnblockConfirmText(item)"
								              ok-text="确认解封" cancel-text="取消" @confirm="onUnblockUser(item)">
									<div :class="['header-btn', 'user-unblock-btn',
									              this.blockingEmail === item.email ? 'is-updating' : '']">
										Unblock
									</div>
								</a-popconfirm>
								<div class="user-blocked-reason">{{ this.getBanReasonText(item) }}</div>
								<div class="user-blocked-sub" v-if="item.banTimeString">{{ item.banTimeString }}</div>
							</div>
						</div>
					</div>
				</template>
			</div>
		</div>

		<!-- 限速输入框。整页只放一个，靠 limitTargetItem 记住在给谁设 -->
		<a-modal v-model:open="this.isLimitModalOpen" title="设置限速"
		         ok-text="确认限速" cancel-text="取消"
		         :confirm-loading="this.limitingEmail !== null"
		         @ok="onSetRateLimit">
			<div class="add-modal-body">
				<div class="add-modal-line">账号：{{ this.limitTargetItem?.email }}（作用于该账号下所有设备）</div>
				<div class="add-modal-line">上行 Mbps（用户发出的数据，留空或 0 为不限）</div>
				<input class="add-days-input" type="number" min="0" step="0.1" v-model="this.limitUpMbps"
				       placeholder="例如 2">
				<div class="add-modal-line">下行 Mbps（发给用户的数据，留空或 0 为不限）</div>
				<input class="add-days-input" type="number" min="0" step="0.1" v-model="this.limitDownMbps"
				       placeholder="例如 4">
				<div class="add-modal-line">持续天数（留空或 0 为不过期）</div>
				<input class="add-days-input" type="number" min="0" step="1" v-model="this.limitDays"
				       placeholder="例如 7" @keyup.enter="onSetRateLimit">
			</div>
		</a-modal>

		<!-- 加时长的输入框。整页只放一个，靠 addTargetItem 记住在给谁加 -->
		<a-modal v-model:open="this.isAddModalOpen" title="增加会员时长"
		         ok-text="确认" cancel-text="取消"
		         :confirm-loading="this.addingEmail !== null"
		         @ok="onAddMembershipConfirm">
			<div class="add-modal-body">
				<div class="add-modal-line">账号：{{ this.addTargetItem?.email }}</div>
				<div class="add-modal-line">当前：{{ this.getPlanLeftText(this.addTargetItem) }}</div>
				<!-- 用原生 input：App.vue 里那条全局的 input {padding: 0 !important}
				     会把 antd 的输入框压扁，自己写的这个能在下面的 scoped 样式里改回来 -->
				<input class="add-days-input" type="number" min="1" step="1"
				       v-model="this.addDays" placeholder="输入要增加的天数"
				       @keyup.enter="onAddMembershipConfirm">
			</div>
		</a-modal>
	</div>
</template>

<script>
import {copyToClipboard, hideLoading, postRequestAsync, showLoading} from "@/utils/methods";
import moment from "moment-timezone";

export default {
	name: "UserPage",
	data() {
		return {
			userList: [],
			searchKeyword: '',
			// 当前列表是搜索结果还是全量列表，空列表的文案不一样
			isSearch: false,
			page: 1,
			pageSize: 20,
			totalCount: 0,
			// 区分"还没查"和"查完是空的"，否则一进页面就会闪一下空列表提示
			isLoaded: false,
			blockingEmail: null,
			mutingEmail: null,
			clearingEmail: null,
			// 加时长的弹窗。addTargetItem 直接存列表里那一行的引用，
			// 加完可以就地改它的到期时间，不用整页重拉
			isAddModalOpen: false,
			addTargetItem: null,
			addDays: '',
			addingEmail: null,
			// 限速弹窗：输入框里是 Mbps，提交时换算成 kbps
			isLimitModalOpen: false,
			limitTargetItem: null,
			limitUpMbps: '',
			limitDownMbps: '',
			limitDays: '',
			limitingEmail: null
		}
	},
	async mounted() {
		await this.getUserList()
	},
	methods: {
		/** kbps 显示成人看的单位；0 表示该方向不限 */
		formatRateKbps(kbps) {
			if (!kbps) {
				return '不限'
			}
			return kbps >= 1000 ? `${+(kbps / 1000).toFixed(2)}Mbps` : `${kbps}Kbps`
		},
		onOpenLimitModal(item) {
			if (!item?.email || this.limitingEmail) {
				return
			}
			this.limitTargetItem = item
			this.limitUpMbps = ''
			this.limitDownMbps = ''
			this.limitDays = ''
			this.isLimitModalOpen = true
		},
		/**
		 * 设置限速。只传邮箱和限速值，账号下的设备由后端按 userId 找，
		 * 在线的设备会立刻被推送生效，离线的下次连上就生效。
		 */
		async onSetRateLimit() {
			const item = this.limitTargetItem
			if (!item?.email || this.limitingEmail) {
				return
			}
			const upKbps = Math.round((parseFloat(this.limitUpMbps) || 0) * 1000)
			const downKbps = Math.round((parseFloat(this.limitDownMbps) || 0) * 1000)
			const durationDays = parseFloat(this.limitDays) || 0
			if (upKbps <= 0 && downKbps <= 0) {
				this.$message.warning('上行和下行至少要设置一个')
				return
			}
			// 和服务端的下限一致，提前拦住比服务端回 304 友好
			if ((upKbps > 0 && upKbps < 64) || (downKbps > 0 && downKbps < 64)) {
				this.$message.warning('限速不能低于 0.064 Mbps')
				return
			}
			this.limitingEmail = item.email
			try {
				const result = await postRequestAsync('admin/user/setRateLimit', {
					email: item.email, upKbps, downKbps, durationDays
				})
				if (result?.code === 200) {
					item.rateLimit = {
						upKbps: result.msg?.upKbps || 0,
						downKbps: result.msg?.downKbps || 0,
						expireTimeString: result.msg?.expireTimeString || ''
					}
					this.isLimitModalOpen = false
					this.$message.success('已限速，在线设备立即生效')
				} else if (result?.code === 303) {
					this.$message.error('用户表里找不到这个账号')
				} else if (result?.code === 304) {
					this.$message.error(result?.msg || '限速参数不合法')
				} else {
					this.$message.error(`限速失败${result?.code ? `（${result.code}）` : ''}`)
				}
			} finally {
				this.limitingEmail = null
			}
		},
		async onClearRateLimit(item) {
			if (!item?.email || this.limitingEmail) {
				return
			}
			this.limitingEmail = item.email
			try {
				const result = await postRequestAsync('admin/user/clearRateLimit', {
					email: item.email
				})
				if (result?.code === 200) {
					item.rateLimit = null
					this.$message.success('已取消限速')
				} else if (result?.code === 303) {
					this.$message.error('用户表里找不到这个账号')
				} else {
					this.$message.error(`取消限速失败${result?.code ? `（${result.code}）` : ''}`)
				}
			} finally {
				this.limitingEmail = null
			}
		},
		async getUserList(isManual = false) {
			showLoading()
			const result = await postRequestAsync('admin/user/getUserList', {
				keyword: this.searchKeyword.trim(),
				page: this.page
			})
			hideLoading()

			if (result?.code === 200) {
				this.userList = result?.msg?.list || []
				this.isSearch = result?.msg?.isSearch === true
				this.totalCount = result?.msg?.totalCount || 0
				this.pageSize = result?.msg?.pageSize || this.pageSize
				this.isLoaded = true
				if (isManual === true) {
					this.$message.success('刷新成功')
				}
			} else {
				this.$message.error(`获取用户列表失败${result?.code ? `（${result.code}）` : ''}`)
			}
		},
		async onRefreshBtnClicked() {
			await this.getUserList(true)
		},
		getListTitle() {
			if (this.isSearch === true) {
				return `搜索结果（${this.totalCount}）`
			}
			return `用户（${this.totalCount}）`
		},
		async onPageChanged(page) {
			this.page = page
			await this.getUserList()
		},
		async onSearchClicked() {
			// 换了关键字，之前那一页的页码就没意义了
			this.page = 1
			await this.getUserList()
		},
		async onResetSearchClicked() {
			if (!this.searchKeyword) {
				return
			}
			this.searchKeyword = ''
			this.page = 1
			await this.getUserList()
		},
		formatTimeString(timestamp) {
			if (!timestamp) {
				return '无'
			}
			return moment(parseInt(timestamp) * 1000).tz('Asia/Shanghai').format('YYYY-MM-DD HH:mm:ss');
		},
		/**
		 * 一个账号可能绑了多台设备，后端返回单个 deviceId 或者一个数组都能显示。
		 */
		getDeviceIdList(item) {
			if (Array.isArray(item?.deviceIdList)) {
				return item.deviceIdList.filter(Boolean)
			}
			if (Array.isArray(item?.deviceIds)) {
				return item.deviceIds.filter(Boolean)
			}
			return [item?.deviceId].filter(Boolean)
		},
		/**
		 * 会员剩余时长。planExpireTime 是秒级时间戳。
		 * 到期时间在过去要明确说"已过期"，不能算成负数天，也不能跟"没买过"混为一谈。
		 */
		getPlanLeftText(item) {
			const expireTime = parseInt(item?.planExpireTime)
			if (!expireTime) {
				return '无套餐'
			}
			const leftSeconds = expireTime - Math.floor(Date.now() / 1000)
			if (leftSeconds <= 0) {
				return `已过期（${this.formatTimeString(expireTime)}）`
			}
			const days = Math.floor(leftSeconds / 86400)
			const hours = Math.floor((leftSeconds % 86400) / 3600)
			const minutes = Math.floor((leftSeconds % 3600) / 60)
			// 只剩几个小时的时候按天取整会显示"0天"，所以小时和分钟都要留着
			if (days > 0) {
				return `${days}天${hours}小时`
			}
			if (hours > 0) {
				return `${hours}小时${minutes}分钟`
			}
			return `${minutes}分钟`
		},
		getPlanStyle(item) {
			const expireTime = parseInt(item?.planExpireTime)
			if (!expireTime) {
				return {'color': 'rgba(230, 213, 184, 0.6)'}
			}
			return {'color': expireTime > Math.floor(Date.now() / 1000) ? '#52c41a' : '#ff6b6b'}
		},
		onAddMembershipClicked(item) {
			if (!item?.email || this.addingEmail) {
				return
			}
			this.addTargetItem = item
			// 每次打开都从空开始，免得把上一个账号填的天数误加到这个账号上
			this.addDays = ''
			this.isAddModalOpen = true
		},
		/**
		 * 给账号加会员时长。
		 *
		 * 天数在前端先挡一道（正整数、不超过 10 年），但后端同样会校验——
		 * 这个接口能直接改会员状态，不能只靠页面上的输入框把关。
		 */
		async onAddMembershipConfirm() {
			const item = this.addTargetItem
			if (!item?.email || this.addingEmail) {
				return
			}

			const days = parseInt(this.addDays)
			if (!days || isNaN(days) || days <= 0) {
				this.$message.error('请输入大于 0 的天数')
				return
			}
			if (days > 3650) {
				this.$message.error('一次最多增加 3650 天')
				return
			}

			this.addingEmail = item.email
			try {
				const result = await postRequestAsync('admin/user/addMembership', {
					email: item.email,
					days: days
				})
				if (result?.code === 200) {
					// 用后端算出来的到期时间，别在前端再算一遍——两边的续期口径
					// （过期的从现在起算）要是有出入，页面显示的就是错的
					item.planExpireTime = result?.msg?.planExpireTime || item.planExpireTime
					this.$message.success(`已增加 ${days} 天会员`)
					this.isAddModalOpen = false
					this.addTargetItem = null
					this.addDays = ''
				} else if (result?.code === 303) {
					this.$message.error('用户表里找不到这个账号')
				} else if (result?.code === 304) {
					this.$message.error('天数不合法')
				} else {
					this.$message.error(`增加失败${result?.code ? `（${result.code}）` : ''}`)
				}
			} finally {
				this.addingEmail = null
			}
		},
		/**
		 * 清空会员的确认文案。把当前剩余时长写进去，
		 * 免得把一个还剩大半年的账号当成过期号清掉。
		 */
		getClearConfirmText(item) {
			return `确认清空 ${item?.email || '该账号'} 的会员？当前${this.getPlanLeftText(item)}`
		},
		/**
		 * 清空这个账号的会员时长。只动 planExpireTime，不碰支付记录。
		 */
		async onClearMembership(item) {
			if (!item?.email || this.clearingEmail) {
				return
			}
			this.clearingEmail = item.email
			try {
				const result = await postRequestAsync('admin/user/clearMembership', {
					email: item.email
				})
				if (result?.code === 200) {
					// 置空后 getPlanLeftText 会显示"无套餐"，和后端写进库里的一致
					item.planExpireTime = ''
					this.$message.success('已清空会员时长')
				} else if (result?.code === 303) {
					this.$message.error('用户表里找不到这个账号')
				} else {
					this.$message.error(`清空失败${result?.code ? `（${result.code}）` : ''}`)
				}
			} finally {
				this.clearingEmail = null
			}
		},
		/**
		 * 封禁原因。
		 *
		 * 名下设备被别人的黑名单记录带上时，原因写的是那个账号的事由，
		 * 直接展示会让人以为是这个人自己干的，所以要标出来源账号。
		 * 老记录没写 note，退回一句"已在黑名单中"，总比空着强。
		 */
		getBanReasonText(item) {
			const reason = item?.banReason || '已在黑名单中'
			if (item?.banByDevice === true) {
				const from = item?.banMatchedEmail ? `${item.banMatchedEmail} ` : ''
				return `${reason}（因设备与 ${from}关联）`
			}
			return reason
		},
		/**
		 * 解除禁言的确认文案。写清楚是立刻恢复发言，不是缩短禁言时长。
		 */
		getUnmuteConfirmText(item) {
			return `确认解除 ${item?.email || '该账号'} 的禁言？（立即恢复在客服渠道发言）`
		},
		/**
		 * 解除禁言。和禁言一样只传邮箱，要解的标识由后端按同一份规则算出来——
		 * 前端自己拼一份的话，少解一个标识人就还是说不了话。
		 */
		async onUnmuteUser(item) {
			if (!item?.email || this.mutingEmail) {
				return
			}
			this.mutingEmail = item.email
			try {
				const result = await postRequestAsync('admin/user/unmuteUser', {
					email: item.email
				})
				if (result?.code === 200) {
					item.isMuted = false
					item.muteUntilTimeString = ''
					this.$message.success('已解除禁言')
				} else if (result?.code === 303) {
					this.$message.error('用户表里找不到这个账号')
				} else {
					this.$message.error(`解除禁言失败${result?.code ? `（${result.code}）` : ''}`)
				}
			} finally {
				this.mutingEmail = null
			}
		},
		/**
		 * 禁言确认框的文案。要说清两件事：时长是一年，以及它跟 Block 不是一回事——
		 * 只是在客服渠道不能发言，账号本身照常能用。
		 */
		getMuteConfirmText(item) {
			return `确认禁言 ${item?.email || '该账号'} 一年？（仅客服渠道不能发言，不影响账号使用）`
		},
		/**
		 * 禁言一年。和封禁一样只传邮箱：要禁的标识（userId、名下设备id）由后端回库查，
		 * 页面上列的那份是展示用的，少显示一台就会漏禁一条发言的路。
		 */
		async onMuteUser(item) {
			if (!item?.email || this.mutingEmail) {
				return
			}
			this.mutingEmail = item.email
			try {
				const result = await postRequestAsync('admin/user/muteUser', {
					email: item.email
				})
				if (result?.code === 200) {
					const banUntil = result?.msg?.banUntilTimeString
					// 就地改这一行的状态：标签和按钮立刻变，不用为一个字段重拉整页
					item.isMuted = true
					item.muteUntilTimeString = banUntil || ''
					this.$message.success(banUntil ? `已禁言，解禁时间 ${banUntil}` : '已禁言一年')
				} else if (result?.code === 303) {
					this.$message.error('用户表里找不到这个账号')
				} else {
					this.$message.error(`禁言失败${result?.code ? `（${result.code}）` : ''}`)
				}
			} finally {
				this.mutingEmail = null
			}
		},
		/**
		 * 封禁确认框的文案。要把"连设备一起封"说清楚——
		 * 只当成封个邮箱按下去，回头会发现这人名下的机器也全进不来了。
		 */
		getBlockConfirmText(item) {
			const deviceCount = this.getDeviceIdList(item).length
			const deviceText = deviceCount > 0 ? `及名下 ${deviceCount} 台设备` : ''
			return `确认把 ${item?.email || '该账号'}${deviceText} 加入黑名单？`
		},
		/**
		 * 把这个账号加进黑名单。设备id 由后端自己回库查，这里只传邮箱：
		 * 页面上列的那份是展示用的，拿它当封禁依据，少显示一台就会少封一台。
		 */
		async onBlockUser(item) {
			if (!item?.email || this.blockingEmail) {
				return
			}
			this.blockingEmail = item.email
			try {
				const result = await postRequestAsync('admin/user/blockUser', {
					email: item.email
				})
				if (result?.code === 200) {
					item.isBanned = true
					const deviceCount = result?.msg?.deviceCount || 0
					this.$message.success(
						deviceCount > 0 ? `已封禁，同时封掉 ${deviceCount} 台设备` : '已封禁'
					)
				} else if (result?.code === 303) {
					this.$message.error('用户表里找不到这个账号')
				} else {
					this.$message.error(`封禁失败${result?.code ? `（${result.code}）` : ''}`)
				}
			} finally {
				this.blockingEmail = null
			}
		},
		/**
		 * 解封的确认文案。要说清连设备一起解——只当成"把邮箱放出来"的话，
		 * 会以为这个人的机器还封着。被别人的记录连坐的那种额外提一句：
		 * 解开它意味着要动另一个账号的封禁记录。
		 */
		getUnblockConfirmText(item) {
			const deviceCount = this.getDeviceIdList(item).length
			const deviceText = deviceCount > 0 ? `及名下 ${deviceCount} 台设备` : ''
			const byDeviceText = item?.banByDevice === true
				? '（这条是被关联账号的记录带上的，解封会把这些设备从那条记录里摘掉）'
				: ''
			return `确认把 ${item?.email || '该账号'}${deviceText} 移出黑名单？${byDeviceText}`
		},
		/**
		 * 解除封禁。和封禁一样只传邮箱，要解的设备由后端回库查——
		 * 前端这份是展示用的，拿它当依据会漏掉没显示出来的那几台，人还是进不来。
		 */
		async onUnblockUser(item) {
			if (!item?.email || this.blockingEmail) {
				return
			}
			this.blockingEmail = item.email
			try {
				const result = await postRequestAsync('admin/user/unblockUser', {
					email: item.email
				})
				if (result?.code === 200) {
					// 就地改这一行：状态、原因、时间一起清掉，
					// 只把 isBanned 置回去的话，下次再封会看到上一次的旧原因
					item.isBanned = false
					item.banReason = ''
					item.banTimeString = ''
					item.banBy = ''
					item.banByDevice = false
					item.banMatchedEmail = ''
					const deviceCount = result?.msg?.deviceCount || 0
					this.$message.success(
						deviceCount > 0 ? `已解封，同时放开 ${deviceCount} 台设备` : '已解封'
					)
				} else if (result?.code === 303) {
					this.$message.error('用户表里找不到这个账号')
				} else {
					this.$message.error(`解封失败${result?.code ? `（${result.code}）` : ''}`)
				}
			} finally {
				this.blockingEmail = null
			}
		},
		/**
		 * 复制任意一段文本。label 只用来拼提示语（"设备ID已复制"），
		 * 复制失败一律提示手动选中——浏览器在非安全上下文里会直接拒绝写剪贴板。
		 */
		async onCopyText(text, label) {
			if (!text) {
				return
			}
			const ok = await copyToClipboard(text)
			if (ok) {
				this.$message.success(`${label}已复制`)
			} else {
				this.$message.error('复制失败，请手动选中复制')
			}
		}
	}
}
</script>

<style scoped>
.user-page-container {
	min-height: 100vh;
	position: relative;
	background-color: #191123;
	overflow-x: hidden;
	width: 100vw;
	padding: 1em 0;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
}

.user-search-container {
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	width: 100%;
	box-sizing: border-box;
	padding: 0 1em;
	gap: 0.6em;
}

.user-search-input {
	flex: 1;
	/* flex 子项默认 min-width:auto，输入框会被内容撑开把按钮挤出屏幕 */
	min-width: 0;
	max-width: 30em;
	color: #E6D5B8;
	background-color: transparent;
	border: 1px solid rgba(230, 213, 184, 0.6);
	border-radius: 0.2em;
	/* App.vue 里有全局的 input {padding: 0 !important}，不加 !important 撑不开 */
	padding: 0.2em 0.8em !important;
	font-size: 1.2em;
	line-height: 1.2em;
	outline: none;
}

.user-search-input:focus {
	border-color: #FF8900;
}

.user-search-input::placeholder {
	color: rgba(230, 213, 184, 0.4);
}

/* 标题 | 分页 | 刷新 三段，窄屏放不下时整行换行 */
.btn-container {
	flex-wrap: wrap;
	row-gap: 0.4em;
	padding: 0 1em;
	margin-bottom: 1em;
}

/* 分页 / 搜索框 / 按钮统一对齐工单页的类目切换按钮：
   同为 font-size 1.2em，高度 = 行高 1.2em + 上下各 0.2em 内边距 + 1px 边框 */
.btn-container :deep(.ant-pagination) {
	font-size: 1.2em;
}

.btn-container :deep(.ant-pagination-simple .ant-pagination-prev),
.btn-container :deep(.ant-pagination-simple .ant-pagination-next),
.btn-container :deep(.ant-pagination-simple .ant-pagination-simple-pager) {
	height: calc(1.6em + 2px);
	line-height: calc(1.6em + 2px);
	min-width: calc(1.6em + 2px);
	margin-inline-end: 0.4em;
}

/* 翻页组最后一项的尾随间距要去掉，否则这一行的右边比其它行少 0.4em，看着不齐 */
.btn-container :deep(.ant-pagination-simple .ant-pagination-next) {
	margin-inline-end: 0;
}

.btn-container :deep(.ant-pagination-simple .ant-pagination-item-link) {
	display: flex;
	align-items: center;
	justify-content: center;
	height: calc(1.6em + 2px);
	min-width: calc(1.6em + 2px);
	border-radius: 0.2em;
	font-size: 1em;
}

.btn-container :deep(.ant-pagination-simple .ant-pagination-simple-pager input) {
	height: calc(1.6em + 2px);
	/* 页码框不画边框，只留数字；宽度收窄到刚好放下页码。
	   App.vue 里有全局的 .ant-pagination.ant-pagination-simple input {border: 1px ... !important}，
	   不加 !important 盖不掉 */
	border: none !important;
	box-shadow: none;
	background-color: transparent;
	min-width: 2em;
	border-radius: 0.2em;
	font-size: 1em;
	line-height: 1.2em;
	text-align: center;
	/* App.vue 里有全局的 input {padding: 0 !important}，不加 !important 撑不开 */
	padding: 0 0.2em !important;
	margin-right: 0.4em;
}

/* 全局 .header-btn 比搜索框矮一截，这里统一成和输入框一样的高度 */
.user-search-btn {
	font-size: 1.2em;
	line-height: 1.2em;
	padding: 0.2em 1em;
	border-radius: 0.2em;
	white-space: nowrap;
}

.user-page-title {
	color: #E6D5B8;
	font-size: 1.2em;
	padding: 0.8em 0;
}

@media only screen and (max-width: 900px) {
	.user-search-container {
		padding: 0 1.2em;
		gap: 0.5em;
	}

	.btn-container {
		padding: 0 1.2em;
	}

	.user-search-btn {
		padding: 0.2em 0.7em;
	}
}

.user-empty {
	color: rgba(230, 213, 184, 0.6);
	padding: 2em 0;
	text-align: center;
	width: 100%;
}

/* item-container 是全局样式，这里只补内边距；#app 上有全局的 text-align: center，
   不显式改回来每一行都会居中 */
.user-row {
	padding: 0.8em;
	cursor: default;
	text-align: left;
	width: 100%;
	/* 不加 min-width:0，长设备ID会把卡片撑出屏幕 */
	min-width: 0;
}

.user-email-line {
	display: flex;
	flex-direction: row;
	align-items: center;
	flex-wrap: wrap;
	gap: 0.4em;
	min-width: 0;
	margin-bottom: 0.3em;
}

.user-email-text {
	color: #E6D5B8;
	font-size: 1.15em;
	word-break: break-all;
}

.user-ban-tag {
	font-size: 0.85em;
	padding: 0.1em 0.5em;
	border-radius: 0.3em;
	border: 1px solid currentColor;
	white-space: nowrap;
}

.user-ban-tag.is-banned {
	color: #ff6b6b;
}

.user-ban-tag.is-normal {
	color: #52c41a;
}

.user-field-line {
	display: flex;
	flex-direction: row;
	align-items: baseline;
	color: rgba(230, 213, 184, 0.85);
	font-size: 0.95em;
	margin-top: 0.2em;
	min-width: 0;
	word-break: break-all;
}

.user-field-label {
	color: rgba(230, 213, 184, 0.5);
	/* 固定宽度让右边的值竖着对齐 */
	flex: 0 0 5em;
}

.user-device-line {
	align-items: flex-start;
}

.user-device-list {
	display: flex;
	flex-direction: column;
	min-width: 0;
	flex: 1;
}

.user-device-item {
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 0.4em;
	min-width: 0;
}

.user-device-id {
	word-break: break-all;
}

.user-device-empty {
	color: rgba(230, 213, 184, 0.5);
}

.user-device-count {
	color: rgba(230, 213, 184, 0.5);
	font-size: 0.9em;
	margin-top: 0.15em;
}

/* 设备多的时候这一栏会很长，单独给它一段行距，不然几十行 id 挤成一坨 */
.user-device-item + .user-device-item {
	margin-top: 0.15em;
}

/* 卡片底部的操作行。width: 100% 不能省：全局 .item-container 上有 align-items: start，
   这一行会收缩成内容宽度，flex-end 就把按钮推不到右边了 */
.user-action-line {
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: flex-end;
	flex-wrap: wrap;
	gap: 0.5em;
	width: 100%;
	margin-top: 0.6em;
}

/* 加时长是给东西，用绿色；旁边的清空是橙、封禁是红，三个按钮一眼分得开 */
.user-add-btn {
	flex: 0 0 auto;
	color: #191123;
	background-color: #52c41a;
	border-color: #52c41a;
	font-size: 0.9em;
	padding: 0.3em 0.9em;
	border-radius: 0.3em;
	white-space: nowrap;
}

.user-add-btn.is-updating {
	opacity: 0.5;
	pointer-events: none;
}

/* 弹窗是 antd 自己的浅色底，这里的文字不能跟着页面用米色 */
.add-modal-body {
	text-align: left;
}

.add-modal-line {
	margin-bottom: 0.5em;
	word-break: break-all;
}

.add-days-input {
	width: 100%;
	box-sizing: border-box;
	border: 1px solid #d9d9d9;
	border-radius: 0.3em;
	/* App.vue 里有全局的 input {padding: 0 !important}，不加 !important 撑不开 */
	padding: 0.5em 0.8em !important;
	font-size: 1em;
	line-height: 1.4em;
	outline: none;
	margin-top: 0.3em;
}

.add-days-input:focus {
	border-color: #FF8900;
}

/* 清会员用页面本来的强调橙，和封禁的红分开：一个是收回权益，一个是把人挡在门外 */
.user-clear-btn {
	flex: 0 0 auto;
	color: #191123;
	background-color: #FF8900;
	border-color: #FF8900;
	font-size: 0.9em;
	padding: 0.3em 0.9em;
	border-radius: 0.3em;
	white-space: nowrap;
}

.user-clear-btn.is-updating {
	opacity: 0.5;
	pointer-events: none;
}

/* 禁言标签用橙色，跟封禁的红分开：两个状态会同时出现，同色就分不清哪个是哪个 */
.user-ban-tag.is-muted {
	color: #FF8900;
}

/* 已禁言时顶掉 Mute 的那块：解除按钮 + 解禁时间。
   必须是 flex column + flex-end——按钮是块级元素，不这么收就会被下面那行
   "禁言至 2027-…" 撑到跟日期一样宽 */
.user-muted-text {
	flex: 0 0 auto;
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	font-size: 0.9em;
	text-align: right;
	white-space: nowrap;
}

/* 实心橙：解除禁言是把人放回来，比 Mute 那个描边按钮更实一点，
   两个状态下按钮长得不一样，一眼能看出这行现在是禁着还是没禁。
   尺寸比同排的 Add/Clear 小一档：它只在被禁言时出现，不该比常驻按钮还抢眼 */
.user-unmute-btn {
	flex: 0 0 auto;
	align-self: flex-end;
	color: #191123;
	background-color: #FF8900;
	border-color: #FF8900;
	font-size: 0.85em;
	padding: 0.15em 0.7em;
	border-radius: 0.3em;
	white-space: nowrap;
}

.user-unmute-btn.is-updating {
	opacity: 0.5;
	pointer-events: none;
}

.user-muted-sub {
	color: rgba(230, 213, 184, 0.5);
	font-size: 0.85em;
	margin-top: 0.2em;
}

/* 限速标签用蓝色：和封禁的红、禁言的橙都分开，三个状态可能同时出现 */
.user-ban-tag.is-limited {
	color: #4da3ff;
}

/* 限速是"降级"不是"处罚"，用蓝色描边，比 Mute 的红色更轻 */
.user-limit-btn,
.user-unlimit-btn {
	flex: 0 0 auto;
	color: #4da3ff;
	background-color: transparent;
	border-color: #4da3ff;
	font-size: 0.9em;
	padding: 0.3em 0.9em;
	border-radius: 0.3em;
	white-space: nowrap;
}

/* 已限速时换成实心，一眼能看出这行现在是限着的 */
.user-unlimit-btn {
	color: #191123;
	background-color: #4da3ff;
}

.user-limit-btn.is-updating,
.user-unlimit-btn.is-updating {
	opacity: 0.5;
	pointer-events: none;
}

/* 描边而不是实心：禁言比封禁轻，挨着 Block 放的时候不该抢它的分量 */
.user-mute-btn {
	flex: 0 0 auto;
	color: #ff6b6b;
	background-color: transparent;
	border-color: #ff6b6b;
	font-size: 0.9em;
	padding: 0.3em 0.9em;
	border-radius: 0.3em;
	white-space: nowrap;
}

.user-mute-btn.is-updating {
	opacity: 0.5;
	pointer-events: none;
}

/* 实心红底 + 页面底色的字：封禁是不可逆的操作，得比卡片里其它元素更显眼 */
.user-block-btn {
	flex: 0 0 auto;
	color: #191123;
	background-color: #ff6b6b;
	border-color: #ff6b6b;
	font-size: 0.9em;
	padding: 0.3em 0.9em;
	border-radius: 0.3em;
	white-space: nowrap;
}

/* 请求还在路上时不让再点第二次 */
.user-block-btn.is-updating {
	opacity: 0.5;
	pointer-events: none;
}

/* 封禁原因是长短不定的文本，给个上限并靠右对齐，
   免得一条长原因把这一行撑得跟按钮错位。
   竖排是因为这块里现在还有 Unblock 按钮，按钮在上原因在下 */
.user-blocked-text {
	flex: 0 0 auto;
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	color: rgba(230, 213, 184, 0.5);
	font-size: 0.9em;
	max-width: 24em;
	text-align: right;
	word-break: break-word;
}

/* 描边而不是实心：解封是把人放回来，不该比同排的 Block 还抢眼。
   尺寸和 Unmute 那个一样小一档——它只在被封时出现，不是常驻按钮 */
.user-unblock-btn {
	flex: 0 0 auto;
	align-self: flex-end;
	color: #6bcf8f;
	background-color: transparent;
	border-color: #6bcf8f;
	font-size: 0.85em;
	padding: 0.15em 0.7em;
	border-radius: 0.3em;
	white-space: nowrap;
	margin-bottom: 0.2em;
}

/* 请求还在路上时不让再点第二次 */
.user-unblock-btn.is-updating {
	opacity: 0.5;
	pointer-events: none;
}

.user-blocked-reason {
	color: #ff6b6b;
}

.user-blocked-sub {
	margin-top: 0.1em;
}

.copy-text-btn {
	width: 1em;
	height: 1em;
	flex: 0 0 auto;
	cursor: pointer;
	opacity: 0.7;
}

.copy-text-btn:hover {
	opacity: 1;
}
</style>
